/* eslint-disable */
import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Delete, Banknote, ChefHat, ArrowLeft } from "lucide-react";
import { toast } from "sonner";
import { api } from "../lib/api";
import { saveAuth, clearAuth } from "../lib/auth";
import { getBranding, subscribeBranding } from "../lib/branding";
import { getDeviceId, getDeviceLabel } from "../lib/device";

const CODE_LENGTH = 4;

export default function Login() {
  const [code, setCode] = useState("");
  const [loading, setLoading] = useState(false);
  const [brand, setBrand] = useState(getBranding());
  const [stage, setStage] = useState("code"); // "code" | "role"
  const [pendingTeam, setPendingTeam] = useState(null);
  const [autofilled, setAutofilled] = useState(false);
  const [slotConflict, setSlotConflict] = useState(null); // { role, message }
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  useEffect(() => {
    clearAuth();
    return subscribeBranding(setBrand);
  }, []);

  // Auto-fill via ?code=XXXX (QR-scan flow)
  useEffect(() => {
    const fromUrl = (searchParams.get("code") || "").trim();
    if (fromUrl && /^\d{4}$/.test(fromUrl) && !autofilled) {
      setAutofilled(true);
      setCode(fromUrl);
      toast.info("Code uit QR ingelezen…");
    }
  }, [searchParams, autofilled]);

  const press = (d) => {
    if (code.length >= CODE_LENGTH) return;
    setCode((p) => p + d);
  };

  const back = () => setCode((p) => p.slice(0, -1));
  const clearAll = () => setCode("");

  useEffect(() => {
    if (stage !== "code") return;
    if (code.length !== CODE_LENGTH) return;
    submitCode(code);
  }, [code, stage]);

  const submitCode = async (value) => {
    setLoading(true);
    try {
      const { data } = await api.post("/auth/login", { code: value });
      if (data.type === "admin") {
        saveAuth("admin", data.token);
        toast.success("Welkom Admin!");
        navigate("/admin");
        return;
      }
      if (data.type === "team" && data.requires_role) {
        setPendingTeam(data.team);
        setStage("role");
        return;
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Ongeldige code");
      setCode("");
    } finally {
      setLoading(false);
    }
  };

  const submitRole = async (role, force = false) => {
    if (!pendingTeam) return;
    setLoading(true);
    try {
      const { data } = await api.post("/auth/login", {
        code,
        role,
        device_id: getDeviceId(),
        device_label: getDeviceLabel(),
        force,
      });
      saveAuth(data.role, data.token, data.team);
      toast.success(`${data.team.naam} · ${role}`);
      setSlotConflict(null);
      navigate(role === "kassa" ? "/kassa" : "/voorbereider");
    } catch (e) {
      const status = e?.response?.status;
      const detail = e?.response?.data?.detail || "Slot bezet of niet beschikbaar";
      if (status === 409) {
        setSlotConflict({ role, message: detail });
      } else {
        toast.error(detail);
      }
    } finally {
      setLoading(false);
    }
  };

  const backToCode = () => {
    setStage("code");
    setCode("");
    setPendingTeam(null);
  };

  const dots = Array.from({ length: CODE_LENGTH }).map((_, i) => i < code.length);

  return (
    <div
      className="min-h-screen flex flex-col items-center justify-center bg-[var(--bg-soft)] px-4 relative"
      data-testid="login-screen"
    >
      <div
        className="absolute top-0 inset-x-0 h-1.5"
        style={{ background: pendingTeam?.kleur || brand.hoofdkleur }}
      />

      <div className="w-full max-w-md tile p-8" data-testid="login-card">
        <div className="flex flex-col items-center mb-6">
          {brand.logo_url ? (
            <img
              src={brand.logo_url}
              alt={brand.bedrijfsnaam}
              className="max-h-20 max-w-[240px] object-contain mb-3"
              data-testid="login-logo"
            />
          ) : (
            <div
              className="font-display font-extrabold text-3xl tracking-tight text-center"
              style={{ color: pendingTeam?.kleur || brand.hoofdkleur }}
              data-testid="login-bedrijfsnaam"
            >
              {brand.bedrijfsnaam}
            </div>
          )}
          <p
            className="text-xs uppercase tracking-[0.25em] text-[var(--ink-soft)] mt-2 font-bold"
            data-testid="login-ondertitel"
          >
            {brand.bedrijfsondertitel}
          </p>
        </div>

        {stage === "code" && (
          <CodeStage
            dots={dots}
            loading={loading}
            press={press}
            back={back}
            clearAll={clearAll}
          />
        )}

        {stage === "role" && pendingTeam && (
          <RoleStage
            team={pendingTeam}
            loading={loading}
            onPick={submitRole}
            onBack={backToCode}
          />
        )}
      </div>

      {stage === "code" && (
        <div className="mt-6 flex flex-wrap items-center justify-center gap-x-5 gap-y-2">
          <button
            onClick={() => navigate("/pickup")}
            className="text-sm underline text-[var(--ink-soft)] hover:text-[var(--ink)]"
            data-testid="open-pickup-link"
          >
            Open klant-pickup scherm →
          </button>
          <button
            onClick={() => navigate("/setup")}
            className="text-sm underline text-[var(--ink-soft)] hover:text-[var(--ink)]"
            data-testid="open-setup-link"
          >
            Device-check (eerst doen) →
          </button>
        </div>
      )}

      {slotConflict && (
        <SlotConflictModal
          team={pendingTeam}
          role={slotConflict.role}
          message={slotConflict.message}
          loading={loading}
          onCancel={() => setSlotConflict(null)}
          onForce={() => submitRole(slotConflict.role, true)}
        />
      )}
    </div>
  );
}

function CodeStage({ dots, loading, press, back, clearAll }) {
  return (
    <>
      <div className="text-center text-xs uppercase tracking-[0.25em] text-[var(--ink-soft)] font-bold mb-3">
        Code (Admin of Team)
      </div>
      <div className="flex items-center justify-center gap-3 mb-6" data-testid="pin-dots">
        {dots.map((on, i) => (
          <div
            key={i}
            className="w-4 h-4 rounded-full transition-all"
            style={{
              background: on ? "var(--ink)" : "transparent",
              border: "2px solid var(--ink)",
            }}
          />
        ))}
      </div>

      <div className="grid grid-cols-3 gap-3">
        {[1, 2, 3, 4, 5, 6, 7, 8, 9].map((n) => (
          <button
            key={n}
            onClick={() => press(String(n))}
            className="pin-key btn-press"
            disabled={loading}
            data-testid={`pin-key-${n}`}
          >
            {n}
          </button>
        ))}
        <button
          onClick={clearAll}
          className="pin-key btn-press text-base"
          disabled={loading}
          data-testid="pin-key-clear"
        >
          Wis
        </button>
        <button
          onClick={() => press("0")}
          className="pin-key btn-press"
          disabled={loading}
          data-testid="pin-key-0"
        >
          0
        </button>
        <button
          onClick={back}
          className="pin-key btn-press flex items-center justify-center"
          disabled={loading}
          data-testid="pin-key-back"
        >
          <Delete className="w-7 h-7" />
        </button>
      </div>
    </>
  );
}

function RoleStage({ team, loading, onPick, onBack }) {
  return (
    <div data-testid="role-stage">
      <button
        onClick={onBack}
        className="btn-press inline-flex items-center gap-1 text-xs font-bold text-[var(--ink-soft)] mb-3"
        data-testid="role-back"
      >
        <ArrowLeft className="w-3 h-3" /> Andere code
      </button>

      <div
        className="rounded-3xl px-5 py-6 mb-5 text-center"
        style={{
          background: team.kleur,
          color: "#fff",
        }}
      >
        <div className="text-xs uppercase tracking-[0.3em] font-bold opacity-90">
          Welkom bij
        </div>
        <div
          className="font-display font-extrabold mt-1"
          style={{ fontSize: "clamp(2.5rem, 8vw, 3.5rem)" }}
          data-testid="role-team-naam"
        >
          {team.naam}
        </div>
        <div className="text-sm opacity-90 mt-1">Kies je rol</div>
      </div>

      <div className="grid grid-cols-1 gap-3">
        <button
          onClick={() => onPick("kassa")}
          disabled={loading}
          className="btn-press flex items-center gap-4 px-5 py-5 rounded-3xl text-white font-display font-extrabold text-2xl text-left disabled:opacity-50"
          style={{ background: team.kleur }}
          data-testid="role-kassa"
        >
          <div className="w-14 h-14 rounded-2xl bg-white/20 flex items-center justify-center">
            <Banknote className="w-8 h-8" />
          </div>
          <div className="flex-1">
            <div>Kassa</div>
            <div className="text-xs font-medium opacity-80 uppercase tracking-widest">
              Bestellingen aannemen
            </div>
          </div>
        </button>

        <button
          onClick={() => onPick("voorbereider")}
          disabled={loading}
          className="btn-press flex items-center gap-4 px-5 py-5 rounded-3xl bg-[var(--ink)] text-white font-display font-extrabold text-2xl text-left disabled:opacity-50"
          data-testid="role-voorbereider"
        >
          <div className="w-14 h-14 rounded-2xl bg-white/15 flex items-center justify-center">
            <ChefHat className="w-8 h-8" />
          </div>
          <div className="flex-1">
            <div>Voorbereider</div>
            <div className="text-xs font-medium opacity-80 uppercase tracking-widest">
              Keuken / pickup
            </div>
          </div>
        </button>
      </div>

      <div className="text-[11px] text-[var(--ink-soft)] text-center mt-4 leading-relaxed">
        Elke rol wordt aan dit apparaat gekoppeld. Een ander apparaat kan deze rol pas pakken
        wanneer admin het slot reset.
      </div>
    </div>
  );
}

function SlotConflictModal({ team, role, message, loading, onCancel, onForce }) {
  // Parse: "Slot bezet: <team> - <Role> draait al op <device>"
  const m = (message || "").match(/draait al op\s+(.+)$/i);
  const deviceLabel = m ? m[1].trim() : null;
  const roleLabel = role === "kassa" ? "Kassa" : "Voorbereider";
  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4 animate-fade-in"
      style={{ background: "rgba(0,0,0,0.65)" }}
      data-testid="slot-conflict-modal"
      onClick={(e) => e.target === e.currentTarget && onCancel()}
    >
      <div
        className="bg-white rounded-[2rem] p-6 max-w-md w-full shadow-2xl"
        style={{ borderTop: `12px solid ${team?.kleur || "#D62828"}` }}
      >
        <div className="text-xs uppercase tracking-[0.3em] text-[var(--ink-soft)] font-bold mb-1">
          {team?.naam || "Team"} · {roleLabel}
        </div>
        <div className="font-display text-2xl font-extrabold text-[var(--danger)] mb-3">
          Dit slot is al bezet
        </div>

        <div className="rounded-2xl bg-[var(--neutral)] px-4 py-3 mb-4">
          <div className="text-xs uppercase tracking-widest text-[var(--ink-soft)] font-bold mb-1">
            Draait nu op apparaat
          </div>
          <div className="font-mono text-sm font-bold break-all" data-testid="slot-conflict-device">
            {deviceLabel || "Onbekend apparaat"}
          </div>
        </div>

        <p className="text-sm text-[var(--ink-soft)] mb-4 leading-relaxed">
          Een ander apparaat is al ingelogd op deze rol. Je kunt het overnemen — dat apparaat
          wordt direct uitgelogd en moet opnieuw aanmelden.
        </p>

        <div className="flex flex-col gap-2">
          <button
            onClick={onForce}
            disabled={loading}
            className="btn-press w-full px-5 py-4 rounded-2xl bg-[var(--danger)] text-white font-extrabold disabled:opacity-50"
            data-testid="slot-conflict-force"
          >
            {loading ? "Bezig…" : "Forceer overname"}
          </button>
          <button
            onClick={onCancel}
            disabled={loading}
            className="btn-press w-full px-5 py-3 rounded-2xl bg-white border-2 border-[var(--neutral)] font-bold"
            data-testid="slot-conflict-cancel"
          >
            Annuleer
          </button>
        </div>

        <div className="text-[11px] text-[var(--ink-soft)] text-center mt-4 leading-relaxed">
          Vraag eerst aan crew of dat apparaat klaar is. Bij twijfel: ga naar Admin → Beveiliging
          om handmatig te resetten.
        </div>
      </div>
    </div>
  );
}

